/**
 * What it reads (spec §2 C) and what it may produce (§2 D).
 *
 * Each evidence source says what leaves the workspace when it is on — the
 * Trust Center line. Reference documents are read before every run, the
 * first 4,000 characters of each (MAX_DOC_CHARS; migration 956 saves no more). The
 * outputs list is fixed by the agent's type: the runner refuses any other
 * write (assertAgentMayProduce).
 */

import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '../../ui/Button';
import { nonEmptyReferenceDocs, type AgentReferenceDoc } from '../../../lib/agentSettingsService';
import type { AgentOutputKind } from '../../../lib/ai/agentDefinitions';
import { SwitchGroup, Toggle, type SectionProps } from './shared';
import { resolveSources } from './resolve';
import { ReferenceDocsEditor } from './ReferenceDocsEditor';

const OUTPUT_WORDS: Record<AgentOutputKind, string> = {
  'proposal:value': 'A proposed value on an item, for a person to accept, change or reject',
  'proposal:classification': 'A proposed category and altitude, for a person to accept or reject',
  'proposal:relationship': 'A proposed relationship between items, for a person to accept or dismiss',
  'proposal:cluster': 'A group of possible duplicates, for a person to accept or dismiss',
  'proposal:item': 'A proposed new item, for a person to approve',
  'proposal:text': 'Draft wording a person applies or discards',
  finding: 'A finding with a severity, for a person to accept or dismiss',
  report: 'A written report',
  inline_feedback: 'Feedback shown while someone edits, saved nowhere',
  notification: 'A message to the people it is set to tell',
  task: 'A task asking a person for something (for example, to say more about an item)',
};


export function ReadsSection({ def, config, isSaving, canEdit, save, docsDescription, docsPlaceholder }: SectionProps & { docsDescription: string; docsPlaceholder: string }) {
  const sources = resolveSources(def, config);
  const [docs, setDocs] = useState<AgentReferenceDoc[]>([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDocs(config?.referenceDocs ?? []);
    setDirty(false);
  }, [config?.referenceDocs]);

  return (
    <>
      <SwitchGroup title="What it may read" lead="Each source says what leaves the workspace when it is on. This is the Trust Center line.">
        {def.evidenceSources.map((src) => (
          <div key={src.key} className="flex items-start justify-between gap-3 border-t border-[var(--rule)] pt-3">
            <div>
              <p className="text-sm font-medium text-[var(--text-pri)]">{src.label}</p>
              <p className="text-[11px] text-[var(--text-ter)]">{src.dataSent}</p>
              {!src.available && src.unavailableReason && <p className="text-[11px] text-[var(--mustard)] mt-0.5">{src.unavailableReason}</p>}
            </div>
            <Toggle on={sources[src.key]} disabled={isSaving || !src.available || !canEdit} label={src.label}
              onChange={() => void save({ evidenceSources: { ...(config?.evidenceSources ?? {}), [src.key]: !sources[src.key] } })} />
          </div>
        ))}
      </SwitchGroup>

      <SwitchGroup
        title="Reference documents"
        lead={docsDescription}
        action={canEdit && dirty ? (
          <Button variant="primary" size="sm" disabled={isSaving} onClick={() => void save({ referenceDocs: nonEmptyReferenceDocs(docs) }, 'Reference documents saved').then(() => setDirty(false))}>
            {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
          </Button>
        ) : undefined}
      >
        {/* The same editor the agent creator mounts; the rows are saved here, on this page. */}
        <ReferenceDocsEditor docs={docs} readOnly={!canEdit} placeholder={docsPlaceholder}
          onChange={(next) => { setDocs(next); setDirty(true); }} />
      </SwitchGroup>

      <SwitchGroup title="What it may produce" lead="Fixed by its type. It cannot write anything else, and nothing it produces takes effect until a person accepts it.">
        <ul className="space-y-1">
          {def.outputs.map((o) => (
            <li key={o} className="text-sm text-[var(--text-sec)]">• {OUTPUT_WORDS[o]}</li>
          ))}
        </ul>
      </SwitchGroup>
    </>
  );
}
